import { useApiQuery } from './useApiQuery';

export interface ShiftLocation {
  id: string;
  shift: {
    id: string;
    actor: {
      id: number;
      email: string;
      fullName: string;
      role: string;
      partner: any;
      enabled: boolean;
      createdAt: string;
      updatedAt: string;
      lastLoginIp: string;
      lastLoginLocation: string;
      lastLoginAt: string;
    };
    partner: {
      id: number;
      name: string;
      orgType: string;
      partnerIdentifier: string;
      legalName: string;
      contact: string;
      website: string;
      createdAt: string;
      updatedAt: string;
      enabledModules: string[];
    };
    status: string;
    startedAt: string;
    endedAt: string | null;
    lastHeartbeatAt: string;
    startLatitude: number;
    startLongitude: number;
    endLatitude: number | null;
    endLongitude: number | null;
    endReason: string | null;
    lastPausedAt: string | null;
    numberOfPauses: number | null;
    createdAt: string;
    updatedAt: string;
  };
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  locationLabel: string;
  recordedAt: string;
  locationSource: string;
  createdAt: string;
}

interface ShiftLocationsResponse {
  success: boolean;
  message: string;
  data: ShiftLocation[];
}

export function useShiftLocations(shiftId: string | undefined) {
  return useApiQuery<ShiftLocationsResponse>({
    queryKey: ['shift-locations', shiftId],
    request: {
      url: `/partner/shifts/${shiftId}/locations`,
      method: 'GET',
    },
    enabled: !!shiftId,
  });
}

// Helper function to process location data for map display
export function processLocationData(locations: ShiftLocation[]) {
  if (!locations.length) return [];

  // Sort locations by recorded time
  const sortedLocations = [...locations].sort((a, b) => 
    new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
  );

  // Convert to format expected by Leaflet
  return sortedLocations.map((location, index) => ({
    id: location.id,
    latitude: location.latitude,
    longitude: location.longitude,
    placeName: location.locationLabel || `Location ${index + 1}`,
    timestamp: location.recordedAt,
    activity: getActivityFromLocation(location, index, sortedLocations.length),
    registrations: 0, // This would need to be calculated from engagement data if needed
    accuracyMeters: location.accuracyMeters,
    locationSource: location.locationSource,
  }));
}

// Helper function to determine activity type based on location data
function getActivityFromLocation(location: ShiftLocation, index: number, totalLocations: number): string {
  if (index === 0) return 'Shift Start';
  if (index === totalLocations - 1) return 'Shift End';
  
  // You could add more sophisticated logic here based on time gaps, accuracy, etc.
  const timeDiff = new Date(location.recordedAt).getTime() - new Date(location.createdAt).getTime();
  if (timeDiff > 5 * 60 * 1000) { // 5 minutes gap
    return 'Lunch Break';
  }
  
  return 'Canvassing';
}

// Helper function to calculate map bounds
export function calculateMapBounds(locations: ShiftLocation[]) {
  if (!locations.length) return null;
  
  const lats = locations.map(loc => loc.latitude);
  const lngs = locations.map(loc => loc.longitude);
  
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  
  return {
    minLat,
    maxLat,
    minLng,
    maxLng,
    center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2] as [number, number],
  };
}
